import React, { lazy, Suspense, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Analytics } from '@vercel/analytics/react'
import { Toaster } from 'sonner'
import { AuthProvider } from './contexts/AuthContext'
import { SignInModalProvider } from './contexts/SignInModalContext'
import { WorkspaceProvider } from './contexts/WorkspaceContext'
import SignInModal from './components/auth/SignInModal'
import FeedbackButton from './components/feedback/FeedbackButton'
import CommandPalette from './components/CommandPalette'
import ChatApp from './components/chat/ChatApp'
import ZenSettingsModal from './components/ZenSettings'
import { useCommandPalette } from './hooks/useCommandPalette'
import { useCommands } from './hooks/useCommands'
import { useChat } from './hooks/useChat'
import { useProviders } from './hooks/useProviders'
import { useAdminMode } from './hooks/useAdminMode'

// Lazy load page components for code splitting
const LandingPage = lazy(() => import('./components'))
const ZenCreatorSimple = lazy(() => import('./components/ZenCreatorSimple'))
const TextLensCreator = lazy(() => import('./components/TextLensCreator'))
const ZenCreator = lazy(() => import('./components/ZenCreator').then(m => ({ default: m.ZenCreator })))
const ZenCreatorSimpleMOCK = lazy(() => import('./components/ZenCreatorSimpleMOCK'))
const ShareViewer = lazy(() => import('./components/ShareViewer'))
const SharesAdmin = lazy(() => import('./pages/SharesAdmin'))
const FeedbackAdmin = lazy(() => import('./pages/FeedbackAdmin'))
const ConceptGraphExplorer = lazy(() => import('./pages/ConceptGraphExplorer'))
const ImageLab = lazy(() => import('./pages/ImageLab'))
const MyShares = lazy(() => import('./pages/MyShares'))
const DemoViewer = lazy(() => import('./components/DemoViewer'))
const EnterprisePage = lazy(() => import('./pages/EnterprisePage'))
const Workspace = lazy(() => import('./components/workspace/Workspace'))
const GuideViewer = lazy(() => import('./components/GuideViewer'))
const ExplorePage = lazy(() => import('./pages/ExplorePage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const LensMdPage = lazy(() => import('./pages/LensMdPage'))
const TestRouter = lazy(() => import('./debug/testing/TestRouter').then(m => ({ default: m.TestRouter })))

// Loading fallback for lazy route chunks.
// Blank warm-bg (no spinner) so already-cached chunks transition invisibly;
// route components render their own domain-aware loading state if they need one.
const PageLoader = () => <div className="min-h-screen bg-warm-bg" aria-hidden="true" />

// Auth callback handler - redirects to home after Supabase processes tokens
const AuthCallback = () => {
  // Supabase will process the tokens via AuthContext
  // Just redirect to home
  return <Navigate to="/" replace />
}

// App shell with global UI elements
function AppShell({ children }: { children: React.ReactNode }) {
  const isAdmin = useAdminMode()
  const commandPalette = useCommandPalette()
  const { clearMessages } = useChat()
  const { refreshProviders } = useProviders()
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  const { commands } = useCommands({
    onOpenChat: () => setIsChatOpen(true),
    onClearChat: clearMessages,
    onRefreshProviders: refreshProviders,
    onOpenSettings: () => setIsSettingsOpen(true),
    isAdmin
  })

  return (
    <div className="App">
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
      <FeedbackButton />
      <CommandPalette
        isOpen={commandPalette.isOpen}
        onClose={commandPalette.close}
        commands={commands}
      />
      <ChatApp
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
      />
      <ZenSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
      <SignInModal />
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#fefefe',
            border: '1px solid #e5e5e5',
            borderRadius: '8px',
          },
        }}
      />
      <Analytics />
    </div>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SignInModalProvider>
          <WorkspaceProvider>
          <AppShell>
            <Routes>
              {/* Main routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/studio" element={<ZenCreatorSimple />} />
              <Route path="/workspace" element={<Workspace />} />
              <Route path="/text-lens" element={<TextLensCreator />} />
              <Route path="/zen-creator" element={<ZenCreator />} />
              <Route path="/zen-mock" element={<ZenCreatorSimpleMOCK />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/lens-md" element={<LensMdPage />} />

              {/* Share & Guide routes */}
              <Route path="/snippet/:shareId" element={<ShareViewer />} />
              <Route path="/guide/:slug" element={<GuideViewer />} />
              <Route path="/explore" element={<ExplorePage />} />
              <Route path="/my-shares" element={<MyShares />} />

              {/* Admin routes */}
              <Route path="/admin/shares" element={<SharesAdmin />} />
              <Route path="/admin/feedback" element={<FeedbackAdmin />} />
              {/* Legacy admin routes - redirect to new paths */}
              <Route path="/shares-admin" element={<Navigate to="/admin/shares" replace />} />
              <Route path="/admin-shares" element={<Navigate to="/admin/shares" replace />} />
              <Route path="/feedback-admin" element={<Navigate to="/admin/feedback" replace />} />
              <Route path="/admin-feedback" element={<Navigate to="/admin/feedback" replace />} />

              {/* Enterprise routes */}
              <Route path="/enterprise" element={<EnterprisePage />} />

              {/* Tool routes */}
              <Route path="/concept-graph" element={<ConceptGraphExplorer />} />
              <Route path="/image-lab" element={<ImageLab />} />
              <Route path="/demo" element={<DemoViewer />} />

              {/* Auth callback */}
              <Route path="/auth/callback" element={<AuthCallback />} />

              {/* Test routes */}
              <Route path="/test/:testMode" element={<TestRouter />} />

              {/* Catch-all - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AppShell>
          </WorkspaceProvider>
        </SignInModalProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
